import Service                                                      from '@/api/service';
import dayjs                                                        from 'dayjs';
import { PermissionManager }                                        from '@/utils/permissions/permissionManager.js';
import { centToEuro, euroToCent, getFirstAndLastName, getKeyValue } from '@/composables/formatData';
import { useFormatInvoice }                                         from '@/composables/invoice.js';
import { usePartnerPercentToAmount }                                from '@/composables/partner.js';

export const followupBonus = 'followup';
export const exclusiveBonus = 'exclusive';
export const getBonuses = [followupBonus, exclusiveBonus];

export const agentType = 'agent';
export const partnerType = 'partner';
export const manualType = 'manual';

export async function disabledCalculationMessage(tenantId, folder, i18n) {

  if (folder.validationByHeadOffice?.key !== 'validated') {

    return i18n('commissions.messages.notValidatedByHeadOffice');
  }

  if (!folder.actSignatureDate) {

    return i18n('commissions.messages.actSignatureUndefined');
  }

  if (dayjs().isBefore(dayjs(folder.actSignatureDate), 'date')) {

    return i18n('commissions.messages.actSignatureFuture');
  }

  if (folder.diagnosticReimbursement?.active === '') {

    return i18n('commissions.messages.missingDiagnosticReimbursement');
  }

  try {

    await Service.getCommissioningRule(tenantId, 'city', folder.id);
  } catch (e) {

    if (e.response?.status === 404) {

      return i18n('commissions.messages.missingIncentiveChoice');
    }
  }

  if (folder.diagnosticReimbursement?.active === '') {

    return i18n('commissions.messages.missingDiagnosticReimbursement');
  }

  const splits = ['clientAgents', 'mandateAgents', 'saleAgents'];
  for (const field of splits) {

    const splitAsNoAgents = !folder[field].length;
    const splitAsMainAgent = folder[field].some(agent => agent.main);
    if (!splitAsNoAgents && !splitAsMainAgent) {

      return i18n('commissions.messages.missingMainAgent');
    }
  }

  if (PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.commissions.conflicts.show)) {

    const conflicts = await Service.getCommissionsConflicts(tenantId, folder.id);
    if (conflicts.length > 0) {

      return i18n('commissions.messages.conflictWithClient');
    }
  }

  return '';
}

export async function useCalculateCommissions(tenantId, folderId, i18n, displayAlert) {

  try {

    await Service.calculateCommissions(tenantId, folderId);

    displayAlert({
      type     : 'success',
      messages : [i18n('commissions.messages.calculation')]
    });
  } catch (e) {

    const errorJson = await e.response.json();

    let details;
    switch (errorJson.message) {
      case 'missingIncentiveChoice' :
      case 'notValidatedByHeadOffice' :
      case 'undefinedDiagnosticReimbursement':
      case 'undefinedActSignatureDate':
      case 'actSignatureInFutur':
      case 'unvalidatedCommissions':
        details = i18n(`commissions.messages.calculationError.details.${ errorJson.message }`);
        break;
      case 'undefinedScaleEntry':
        const user = await Service.getUser(tenantId, errorJson.actor.userId);
        details = i18n(`commissions.messages.calculationError.details.${ errorJson.message }`, { userName : getFirstAndLastName(user) });
        break;
      default :
        details = errorJson.message;
    }

    const message = i18n('commissions.messages.calculationError.intro', { message : details });

    displayAlert({
      type     : 'error',
      messages : [message]
    });

    throw { ...errorJson, customMessage : message };
  }
}

export function useformatCalculationData(tenant, folder) {

  return Service.formatCalculationData(tenant, folder.id);
}

export async function useGetCommissionData(tenant, folder, commissioningRules = []) {

  try {

    const proformas = await Service.getProformasWithoutPagination(tenant, {
      orderBy        : 'createdAt',
      'deleted:eq'   : false,
      'dossierId:eq' : folder.id
    });

    const commissionsData = await Service.getCommissionsWithoutPagination(tenant, {
      'dossierId:eq' : folder.id,
      'deleted:eq'   : false
    });

    const partnerCommissionData = await Service.getPartnerCommissionsWithoutPagination(tenant, {
      'dossierId:eq' : folder.id,
      'deleted:eq'   : false
    });

    const manualCommissions = await Service.getManualCommissionsWithoutPagination(tenant, {
      'dossierId:eq' : folder.id
    });

    const typedCommissionDatas = commissionsData.map((commission) => {

      return {
        ...commission,
        type : agentType
      };
    });

    const typedPartnerCommissionDatas = partnerCommissionData.map((commission) => {

      return {
        ...commission,
        type : partnerType
      };
    });

    const typedManualCommissionDatas = manualCommissions.map((commission) => {

      return {
        ...commission,
        type : manualType
      };
    });

    const commissions = await useFormatCommissions(
      tenant,
      proformas,
      [...typedCommissionDatas, ...typedPartnerCommissionDatas, ...typedManualCommissionDatas],
      commissioningRules
    );

    let calculationDate = '';
    let createdAt = '';

    if (folder.isCommissionsCalculated && folder.history.calculations) {

      for (const historyCalculation of folder.history?.calculations) {

        if (historyCalculation.date > createdAt) {

          createdAt = historyCalculation.date;
          calculationDate = historyCalculation.actSignatureDate;
        }
      }
    }

    return { calculationDate, createdAt, commissions };
  } catch (e) {

    throw new Error(e);
  }
}

export function useGetTotalCommission(commissions) {

  let total = 0;
  commissions.map(commission => total += commission.totalCommission);

  return total;
}

export function useGetTotalCharges(folder) {

  let charges = Number(folder.clientReimbursement);

  if (folder.diagnosticReimbursement.value) {
    charges += Number(folder.diagnosticReimbursement.value);
  }

  if (folder.buyerPartnerLead.amount) {
    charges += folder.buyerPartnerLead.amount.percent ? usePartnerPercentToAmount('buyerPartnerLead', folder.buyerPartnerLead.amount.value, folder) : folder.buyerPartnerLead.amount.value;
  }

  if (folder.mandatePartnerLead.amount) {
    charges += folder.mandatePartnerLead.amount.percent ? usePartnerPercentToAmount('mandatePartnerLead', folder.mandatePartnerLead.amount.value, folder) : folder.mandatePartnerLead.amount.value;
  }

  return charges;
}

export function useGetCalculationBasis(folder) {

  return folder.feesHT - useGetTotalCharges(folder);
}

export async function useFormatCommissions(tenantId, proformas, commissions, commissioningRules = []) {

  const formattedCommissions = [];

  for (const commission of commissions) {

    const proforma = proformas.find(proforma => proforma.commissionDataId === commission.id);
    switch (commission.type) {
      case agentType :
        formattedCommissions.push(await useFormatCommissionData(tenantId, proforma, commission, commissioningRules));
        break;
      case partnerType :
        formattedCommissions.push(await useFormatPartnerCommission(tenantId, proforma, commission));
        break;
      case manualType :
        formattedCommissions.push(await useFormatManualCommission(tenantId, proforma, commission));
        break;
    }
  }

  const agents = formattedCommissions.filter((formattedCommission) => !formattedCommission.agent?.value).sort((a, b) => {

    return new Date(a.createdAt) - new Date(b.createdAt) || a.agent?.value.localeCompare(b.agent?.value);
  });

  const partners = formattedCommissions.filter((formattedCommission) => formattedCommission.agent?.value).sort((a, b) => {

    return new Date(a.createdAt) - new Date(b.createdAt) || a.agent?.value.localeCompare(b.agent?.value);
  });
  return [...agents, ...partners];
}

async function useFormatCommissionData(tenantId, proforma, commission, rules) {

  const result = {
    id              : commission.id,
    createdAt       : commission.createdAt,
    agent           : await getKeyValue(tenantId, commission.userId),
    totalCommission : centToEuro(commission.total),
    details         : await formatDetails(tenantId, commission.details),
    bonuses         : commission.bonuses.map(bonus => {

      const findBonus = rules.find(rule => rule.commissioningRulesType === bonus.type)?.data;
      return {
        bdd          : bonus,
        movementType : bonus.type,
        amount       : centToEuro(bonus.value),
        rolling      : findBonus?.value,
        percent      : findBonus?.distribution[commission.userId]
      };
    }),
    additions       : commission.additions.map(addition => {

      return {
        amount  : centToEuro(addition.amount),
        comment : addition.comment
      };
    }),
    note            : commission.note,
    type            : commission.type,
    owner : commission.userId
  };

  if (proforma) {
    const invoice = proforma.invoiceId ? await Service.getInvoice(tenantId, proforma.invoiceId) : null;
    result.proforma = {
      ...proforma,
      amount  : proforma.amount ? centToEuro(proforma.amount) : undefined,
      invoice : invoice ? await useFormatInvoice(tenantId, invoice) : null
    };
  }

  return result;
}

async function formatDetails(tenantId, details) {

  const formattedDetails = [];
  for (const detail of details) {

    let usedRolling = '';

    if (detail.scale?.rolling) {

      usedRolling = detail.scale.rolling.type === 'count' ? detail.scale.rolling.current : centToEuro(detail.scale.rolling.current);
    }

    formattedDetails.push({
      bdd              : detail,
      movementType     : detail.movementType,
      type             : detail.type,
      totalCharges     : detail.calcBase?.totalCharges ? centToEuro(detail.calcBase?.totalCharges) : '',
      calculationBasis : detail.calcBase?.value ? centToEuro(detail.calcBase?.value) : '',
      usedRolling,
      rolling          : detail.scale && detail.scale.level.percent ? detail.scale.level.value : '',
      percent          : detail.percent,
      rollingIncrement : detail.movement?.value || detail.movement?.value === 0 ? centToEuro(detail.movement.value) : '',
      amount           : centToEuro(detail.commission),
      user             : await getKeyValue(tenantId, detail.userId)
    });
  }

  return formattedDetails;
}

export function useUnformatCommissionDetails(details) {

  return details.map(detail => {

    const formatDetail = {
      ...detail.bdd,
      commission : euroToCent(detail.amount)
    };

    delete formatDetail.history;

    if (detail.totalCharges !== undefined && detail.totalCharges !== '') {

      formatDetail.calcBase = {
        ...formatDetail.calcBase,
        totalCharges : euroToCent(detail.totalCharges)
      };
    }

    if (detail.calculationBasis) {

      formatDetail.calcBase = {
        ...formatDetail.calcBase,
        value : euroToCent(detail.calculationBasis)
      };
    }

    if (detail.percent || detail.percent === 0) {

      formatDetail.percent = detail.percent;
    }

    if (detail.rollingIncrement) {

      formatDetail.movement.value = euroToCent(detail.rollingIncrement);
    }

    if (formatDetail.scale) {

      formatDetail.scale.level.value = detail.rolling || 0;
    }

    return formatDetail;
  });
}

export function useUnformatCommissionBonuses(bonuses) {

  return bonuses.map(bonus => {

    const bdd = {
      ...bonus.bdd,
      value : euroToCent(bonus.amount)
    };

    delete bdd.history;

    return bdd;
  });
}

export function useUnformatCommissionAdditions(additions) {

  return additions.map(addition => {

    return {
      amount  : euroToCent(addition.amount),
      comment : addition.comment
    };
  });
}

async function useFormatManualCommission(tenantId, proforma, commission) {

  const result = {
    id              : commission.id,
    createdAt       : commission.createdAt,
    agent           : await getKeyValue(tenantId, commission.userId),
    totalCommission : centToEuro(commission.total),
    additions       : commission.lines.map(addition => {

      return {
        amount  : centToEuro(addition.amount),
        comment : addition.comment
      };
    }),
    note            : commission.note,
    type            : commission.type,
    owner : commission.userId
  };

  if (proforma) {

    const invoice = proforma.invoiceId ? await Service.getInvoice(tenantId, proforma.invoiceId) : null;
    result.proforma = {
      ...proforma,
      amount  : proforma.amount ? centToEuro(proforma.amount) : undefined,
      invoice : invoice ? await useFormatInvoice(tenantId, invoice) : null
    };
  }

  return result;
}

async function useFormatPartnerCommission(tenantId, proforma, commission) {

  const partner = await getKeyValue(tenantId, commission.recipient.id, partnerType);
  let apporteur;
  if (commission.recipient.type === 'ada') {
    apporteur = await Service.getAda(tenantId, partner.key);
  }

  const result = {

    id              : commission.id,
    agent           : partner,
    createdAt       : commission.createdAt,
    totalCommission : centToEuro(commission.total),
    commission      : {
      value : centToEuro(commission.commission.value),
      bdd   : commission.commission
    },
    additions       : commission.additions.map(addition => {

      return {
        amount  : centToEuro(addition.amount),
        comment : addition.comment
      };
    }),
    note            : commission.note,
    type            : commission.type,
    owner : apporteur?.agent ?? commission.recipient.id
  };

  if (proforma) {

    const invoice = proforma.invoiceId ? await Service.getInvoice(tenantId, proforma.invoiceId) : null;
    result.proforma = {
      ...proforma,
      amount  : proforma.amount ? centToEuro(proforma.amount) : undefined,
      invoice : invoice ? await useFormatInvoice(tenantId, invoice) : null
    };
  }

  return result;
}

export function useUnformatManualCommission(newCommission, oldCommission) {

  const fieldsUpdated = {};

  for (const field of Object.keys(newCommission)) {

    switch (field) {
      case 'user' :
        if (JSON.stringify(newCommission[field]) !== JSON.stringify(oldCommission[field])) {
          fieldsUpdated.userId = newCommission[field].key ? newCommission[field].key : null;
        }

        break;

      case 'lines' :
        if (JSON.stringify(newCommission[field]) !== JSON.stringify(oldCommission[field])) {
          fieldsUpdated[field] = useUnformatCommissionAdditions(newCommission[field]);
        }

        break;

      case 'recipient' :
        const oldRecipient = oldCommission[field] ? oldCommission[field] : {};
        fieldsUpdated[field] = useUnformatManualCommissionRecipient(newCommission[field], oldRecipient);

        break;
    }
  }

  return fieldsUpdated;
}

function useUnformatManualCommissionRecipient(newRecipient, oldRecipient) {

  const fieldsUpdated = {};

  for (const field of Object.keys(newRecipient)) {

    switch (field) {
      case 'firstName' :
      case 'lastName' :
      case 'email' :
      case 'siret' :
        if (JSON.stringify(newRecipient[field]) !== JSON.stringify(oldRecipient[field])) {
          fieldsUpdated[field] = newRecipient[field] === '' ? null : newRecipient[field];
        }

        break;

      case 'address' :
        if (JSON.stringify(newRecipient[field]) !== JSON.stringify(oldRecipient[field])) {
          fieldsUpdated[field] = newRecipient[field] === '' ? null : newRecipient[field];
          delete fieldsUpdated[field].coordinates;
        }

        break;
    }
  }

  return fieldsUpdated;
}

export async function hasProformaUpdatedManually(tenantId, folder) {

  try {

    const { commissions } = await useGetCommissionData(tenantId, folder);

    for (const commission of commissions) {

      if (commission.id && commission.type === manualType || commission.additions?.length > 0) {

        return true;
      }
    }

    return false;
  } catch {

    return false;
  }
}
